/**
 * Shared Zoom meeting utilities.
 * Used by WorkspaceTabs (Meetings tab), MeetingReview, and LiveOperations reuse.
 */

// ── Conservative booking-confirmation noise filter ────────────────────────────
// Only suppress known scheduling-system generated titles — NOT any title
// containing "booking" or "confirmed" as a word.
const _BOOKING_NOISE = [
  /^your\s+booking\s+with\b/i,                // "Your booking with Expat Tax Firm is confirmed!"
  /booking\s+(?:is\s+)?confirmed[!.]?\s*$/i,  // "… is confirmed!"
  /^booking\s+confirmation\s*[:—]/i,           // "Booking Confirmation: …"
  /^appointment\s+confirmed[!.]?\s*$/i,        // bare "Appointment confirmed"
  /^meeting\s+confirmed[!.]?\s*$/i,            // bare "Meeting confirmed"
];
function _isMeaninglessTitle(t) {
  if (!t || !t.trim()) return true;
  return _BOOKING_NOISE.some(p => p.test(t.trim()));
}

/**
 * Return the best available meeting date from any transcript record.
 * Priority: meeting_context.zoom.started_at → scheduled_at → transcript.started_at → created_at
 */
export function zoomMeetingTime(transcript) {
  const zoom = transcript?.meeting_context?.zoom || {};
  return zoom.started_at || zoom.scheduled_at || transcript?.started_at || transcript?.created_at || null;
}

/**
 * Return a meaningful display title for the Zoom Meetings section.
 * Suppresses known scheduling-confirmation noise; falls back to
 * "Zoom Meeting — {clientName}" when no better title exists.
 *
 * @param {object} transcript  - transcript record from /related
 * @param {string} [clientName] - full name of the client/lead for deterministic fallback
 */
export function zoomMeetingTitle(transcript, clientName) {
  const zoom = transcript?.meeting_context?.zoom || {};
  const topic = (zoom.topic || "").trim();
  const raw   = (transcript?.title || "").trim();
  if (topic && !_isMeaninglessTitle(topic)) return topic;
  if (raw   && !_isMeaninglessTitle(raw))   return raw;
  return clientName ? `Zoom Meeting — ${clientName}` : "Zoom Meeting";
}

/**
 * Return a source-specific display title for the Meeting Transcripts section.
 * Zoom transcripts always use a "Zoom Meeting Transcript — {name}" pattern.
 * Uploaded transcripts use their stored title when meaningful.
 *
 * @param {object} tr          - transcript record (may be from either list)
 * @param {string} [clientName]
 */
export function meetingTranscriptTitle(tr, clientName) {
  if (tr?.provider === "zoom") {
    return clientName ? `Zoom Meeting Transcript — ${clientName}` : "Zoom Meeting Transcript";
  }
  const platform = tr?.source_platform || "";
  const stored   = (tr?.title || "").trim();
  if (stored && !_isMeaninglessTitle(stored)) return stored;
  const prefixMap = {
    teams:       "Teams Meeting Transcript",
    google_meet: "Google Meet Transcript",
    phone:       "Phone Call Transcript",
    in_person:   "In-Person Meeting Transcript",
  };
  const prefix = prefixMap[platform] || "Meeting Transcript";
  return clientName ? `${prefix} — ${clientName}` : prefix;
}

/**
 * Return { label, tone } for a canonical analysis status, or null if status
 * doesn't exist or doesn't warrant a badge in the meetings context.
 * Uses explicit labels — "Analysis Ready for Review" not generic "Ready For Review".
 */
export function analysisStatusInfo(anaStatus) {
  if (!anaStatus) return null;
  return {
    "ready_for_review": { label: "Analysis Ready for Review", tone: "warning"  },
    "ai_case_analysis":  { label: "Analysis in Progress",      tone: "info"    },
    "analysis_started":  { label: "Analysis in Progress",      tone: "info"    },
    "approved":          { label: "Analysis Approved",         tone: "success" },
    "rejected":          { label: "Analysis Rejected",         tone: "danger"  },
  }[anaStatus] || null;
}

/**
 * Return true when transcript text is inferred to be available for viewing.
 * Inferred conservatively from canonical state — no additional field needed.
 * analysis_id present = Allison was given the transcript = text was captured.
 */
export function hasTranscriptText(tr) {
  return !!(tr?.analysis_id);
}

/**
 * Return true when the transcript has a canonical Zoom meeting occurrence link.
 * Requires both provider=zoom AND an actual meeting start time (confirmed occurrence).
 * Does NOT fire merely because the record came from the zoom_transcripts array.
 */
export function hasLinkedZoomMeeting(tr) {
  return !!(tr?.provider === "zoom" && tr?.meeting_context?.zoom?.started_at);
}

/**
 * Return { label, to } for the state-aware analysis action button, or null if no safe route exists.
 */
export function zoomAnalysisAction(transcript, clientId) {
  if (!transcript?.analysis_id) return null;
  const analysisStatus = transcript?.analysis?.status;
  const to = `/case-analysis/${clientId}`;
  if (analysisStatus === "ready_for_review") return { label: "Review Analysis", to };
  if (analysisStatus === "approved")         return { label: "View Analysis",   to };
  return { label: "View Meeting", to };
}

/**
 * Derive source label + display tone from canonical transcript provenance metadata.
 */
export function transcriptSourceInfo(tr) {
  const provider = tr?.provider  || "";
  const platform = tr?.source_platform || "";
  if (provider === "zoom")        return { label: "Zoom",              tone: "info"    };
  if (provider === "fireflies")   return { label: "Fireflies",         tone: "info"    };
  if (platform === "teams")       return { label: "Teams",             tone: "info"    };
  if (platform === "google_meet") return { label: "Google Meet",       tone: "success" };
  if (platform === "phone")       return { label: "Phone Call",        tone: "neutral" };
  if (platform === "in_person")   return { label: "In Person",         tone: "neutral" };
  if (tr?.ingestion_source === "staff_upload") return { label: "Uploaded Transcript", tone: "neutral" };
  return { label: "Meeting Transcript", tone: "neutral" };
}

/**
 * Format duration minutes as a compact string.
 */
export function fmtDuration(minutes) {
  if (!minutes) return null;
  return minutes >= 60
    ? `${Math.floor(minutes / 60)}h ${minutes % 60}m`
    : `${minutes} min`;
}

/**
 * Parse "Speaker: text. Speaker: text…" preview string into [{speaker, text}].
 */
export function parseSpeakerTurns(previewText, max = 4) {
  if (!previewText) return [];
  const turns = [];
  const re = /([A-Z][^:]{1,60}):\s+(.+?)(?=\s+[A-Z][^:]{1,60}:|$)/g;
  let m;
  while ((m = re.exec(previewText)) !== null) {
    turns.push({ speaker: m[1].trim(), text: m[2].trim().replace(/\.\s*$/, "") });
    if (turns.length >= max) break;
  }
  if (turns.length === 0) turns.push({ speaker: null, text: previewText });
  return turns;
}

/** Parse newline-separated "Speaker: text" transcript into line objects. */
export function parseTranscriptLines(text) {
  if (!text) return [];
  return text.split("\n").filter(l => l.trim()).map(l => {
    const ci = l.indexOf(": ");
    if (ci > 0 && ci < 60) return { speaker: l.slice(0, ci).trim(), text: l.slice(ci + 2).trim() };
    return { speaker: null, text: l.trim() };
  });
}
